import {
  argbFromHex,
  Hct,
  TonalPalette,
} from '@material/material-color-utilities';

import Luiv3Constants from '../../lui3/constants';

export const makePalette = (primary: number) => {
  const hct = Hct.fromInt(primary);
  const hue = hct.hue;
  return {
    a1: TonalPalette.fromHueAndChroma(hue, Math.max(48, hct.chroma)),
    a2: TonalPalette.fromHueAndChroma(hue - 30, 8), // hue, 16
    a3: TonalPalette.fromHueAndChroma(hue + 60, 24),
    n1: TonalPalette.fromHueAndChroma(hue - 30, 16), // hue, 4
    n2: TonalPalette.fromHueAndChroma(hue - 30, 8), // hue, 8
    error: TonalPalette.fromHueAndChroma(15, 88),
    link: TonalPalette.fromHueAndChroma(227, 48),
  };
};

export const colorLightTone = 45; // 40
export const onColorLightTone = 100; // 100
export const containerLightTone = 95; // 90
export const onContainerLightTone = 45; // 10

const onSurfaceLightTone = 30; // 10

/**
 * Light scheme from core palette
 */
export const lightFromCorePalette = (core: ReturnType<typeof makePalette>) => ({
  primary: core.a1.tone(colorLightTone + 9),
  onPrimary: core.a1.tone(onColorLightTone),
  primaryContainer: core.a1.tone(containerLightTone),
  onPrimaryContainer: core.a1.tone(onContainerLightTone),

  secondary: core.a2.tone(colorLightTone),
  onSecondary: core.a2.tone(onColorLightTone),
  secondaryContainer: core.a2.tone(85),
  onSecondaryContainer: core.a2.tone(onContainerLightTone),

  tertiary: core.a3.tone(colorLightTone),
  onTertiary: core.a3.tone(onColorLightTone),
  tertiaryContainer: core.a3.tone(containerLightTone),
  onTertiaryContainer: core.a3.tone(onContainerLightTone),

  error: core.error.tone(colorLightTone + 5),
  onError: core.error.tone(onColorLightTone),
  errorContainer: core.error.tone(containerLightTone),
  onErrorContainer: core.error.tone(onContainerLightTone),

  link: argbFromHex(Luiv3Constants.baseColor['blue-600']), // custom
  onLink: core.link.tone(onColorLightTone), // custom
  linkContainer: core.link.tone(containerLightTone), // custom
  onLinkContainer: core.link.tone(onContainerLightTone), // custom

  background: core.n1.tone(100), // 99
  onBackground: core.n1.tone(onSurfaceLightTone),

  surface: core.n1.tone(100), // 99
  onSurface: core.n1.tone(onSurfaceLightTone),
  onSurfaceBold: core.n1.tone(0), // custom

  surfaceVariant: core.n2.tone(90), // 90
  onSurfaceVariant: core.n2.tone(30), // 30

  outline: core.n2.tone(85), // 50
  divider: core.n2.tone(90),

  shadow: core.n1.tone(0), // 0

  inverseSurface: core.n1.tone(10), // 20
  inverseSurfaceContainer: core.n1.tone(containerLightTone),
  onInverseSurfaceContainer: core.n1.tone(onContainerLightTone),
  inverseOnSurface: core.n1.tone(95), // 95
  inversePrimary: core.a1.tone(80), // 80
});

export const colorDarkTone = 80; // 80
export const onColorDarkTone = 20; // 20
export const containerDarkTone = 30; // 30
export const onContainerDarkTone = 90; // 90

const onSurfaceDarkTone = 90; // 90

/**
 * Dark scheme from core palette
 */
export const darkFromCorePalette = (core: ReturnType<typeof makePalette>) => ({
  primary: core.a1.tone(colorDarkTone),
  onPrimary: core.a1.tone(onColorDarkTone),
  primaryContainer: core.a1.tone(containerDarkTone),
  onPrimaryContainer: core.a1.tone(onContainerDarkTone),

  secondary: core.a2.tone(colorDarkTone),
  onSecondary: core.a2.tone(onColorDarkTone),
  secondaryContainer: core.a2.tone(containerDarkTone),
  onSecondaryContainer: core.a2.tone(onContainerDarkTone),

  tertiary: core.a3.tone(colorDarkTone),
  onTertiary: core.a3.tone(onColorDarkTone),
  tertiaryContainer: core.a3.tone(containerDarkTone),
  onTertiaryContainer: core.a3.tone(onContainerDarkTone),

  error: core.error.tone(colorDarkTone),
  onError: core.error.tone(onColorDarkTone),
  errorContainer: core.error.tone(containerDarkTone),
  onErrorContainer: core.error.tone(onContainerDarkTone),

  link: core.link.tone(colorDarkTone), // custom
  onLink: core.link.tone(onColorDarkTone), // custom
  linkContainer: core.link.tone(containerDarkTone), // custom
  onLinkContainer: core.link.tone(onContainerDarkTone), // custom

  background: core.n1.tone(10), // 10
  onBackground: core.n1.tone(onSurfaceDarkTone),

  surface: core.n1.tone(10), // 10
  onSurface: core.n1.tone(onSurfaceDarkTone),
  onSurfaceBold: core.n1.tone(100), // custom

  surfaceVariant: core.n2.tone(30), // 30
  onSurfaceVariant: core.n2.tone(80), // 80

  outline: core.n2.tone(40), // 60
  divider: core.n2.tone(20),

  shadow: core.n1.tone(0), // 0

  inverseSurface: core.n1.tone(90), // 90
  inverseSurfaceContainer: core.n1.tone(containerDarkTone),
  onInverseSurfaceContainer: core.n1.tone(onContainerDarkTone),
  inverseOnSurface: core.n1.tone(20), // 20
  inversePrimary: core.a1.tone(40), // 40
});
